import axios from "axios";
import i18n from "i18next";

import { getUrlApi } from "../utilities/util";

/**
 * Cliente do log de atividade.
 *
 * NÃO USA O `ApiHandler`, E ISSO É O PONTO MAIS IMPORTANTE DESTE ARQUIVO.
 *
 * O interceptor de resposta do ApiHandler chama `logout()` em QUALQUER 401
 * (`ApiHandlers/ApiHandler.ts:67-70`). Como o coletor descarrega em segundo plano, um flush
 * que pegasse a sessão expirada expulsaria o usuário da aplicação e o mandaria para o login —
 * telemetria derrubando a sessão de quem está trabalhando. É a pior falha possível desta
 * feature, e a única defesa é não passar por lá.
 *
 * Segue o padrão do `SecurityApi`: axios direto com `withCredentials`, que é o que leva o
 * cookie httpOnly `accessToken`.
 */

const baseURL = String(getUrlApi() ?? "");

/** Mesmo mapeamento de cultura do ApiHandler — a rota da API é prefixada pelo idioma. */
const cultureSegment = (): string =>
  (i18n.language || "pt-br").toLowerCase().startsWith("en") ? "en-us" : "pt-br";

const batchUrl = (): string => `${baseURL}/${cultureSegment()}/api/ActivityLog/batch`;

export interface ActivityLogEventPayload {
  eventType: number;
  screenKey: string;
  routePattern: string;
  /** JSON já serializado, ou null. */
  payload: string | null;
  /** ISO 8601 em UTC. */
  occurredAt: string;
}

export interface ActivityLogBatchPayload {
  sessionId: string;
  events: ActivityLogEventPayload[];
}

/** Resultado do envio, para o coletor decidir entre desistir do lote ou parar de vez. */
export type SendOutcome = "sent" | "dropped" | "unauthorized";

export default class ActivityLogApi {
  /**
   * Envio normal (flush por tempo/contagem/tamanho).
   *
   * Nunca lança: erro de log não pode escapar para o caminho da aplicação. O chamador
   * distingue `unauthorized` (para de tentar até a próxima sessão) de `dropped` (falha
   * pontual, descarta o lote e segue).
   */
  static async SendBatch(batch: ActivityLogBatchPayload): Promise<SendOutcome> {
    try {
      await axios.post(batchUrl(), batch, { withCredentials: true });
      return "sent";
    } catch (error: any) {
      return error?.response?.status === 401 ? "unauthorized" : "dropped";
    }
  }

  /**
   * Envio no fechamento da aba.
   *
   * `fetch` com `keepalive` e NÃO `sendBeacon`: o beacon com corpo JSON em cross-origin
   * dispara preflight OPTIONS durante o unload — o cenário menos confiável do navegador — e
   * não devolve status nenhum, só um booleano de enfileiramento. E a topologia difere entre
   * ambientes: em dev a baseURL é absoluta e cross-origin (`:3000` contra `:6002`), em prod é
   * vazia e same-origin.
   *
   * O guard de `fetch` espelha o que `Auth.ts` já faz para `sendBeacon`: o jsdom não
   * implementa essas APIs, e sem o guard o teste do flush de fechamento quebra.
   */
  static SendBatchOnUnload(batch: ActivityLogBatchPayload): void {
    if (typeof fetch !== "function") return;

    try {
      void fetch(batchUrl(), {
        method: "POST",
        credentials: "include",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(batch),
      }).catch(() => {
        // Silencioso de propósito: no unload não há para quem reportar, e uma promise
        // rejeitada sem catch polui o console do usuário na saída.
      });
    } catch {
      // idem
    }
  }
}
